/**
 * BoardFileItem - Individual file display in the files panel
 */

import { clsx } from 'clsx';
import {
  File,
  Image,
  FileText,
  Film,
  Music,
  Archive,
  Trash2,
  Download,
  Eye,
  MoreVertical,
} from 'lucide-react';
import { formatDistanceToNow } from '@/i18n/dateFns';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import type { Attachment } from '@services/api/boardFiles';

interface BoardFileItemProps {
  attachment: Attachment;
  onView: () => void;
  onDownload: () => void;
  onDelete: () => void;
}

// File type icons mapping
const getFileIcon = (contentType: string | undefined) => {
  if (!contentType) return File;
  if (contentType.startsWith('image/')) return Image;
  if (contentType.startsWith('video/')) return Film;
  if (contentType.startsWith('audio/')) return Music;
  if (contentType.includes('pdf') || contentType.includes('document') || contentType.includes('text'))
    return FileText;
  if (contentType.includes('zip') || contentType.includes('archive') || contentType.includes('compressed'))
    return Archive;
  return File;
};

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export default function BoardFileItem({
  attachment,
  onView,
  onDownload,
  onDelete,
}: BoardFileItemProps) {
  const FileIcon = getFileIcon(attachment.contentType);
  const isImage = attachment.contentType?.startsWith('image/');
  const isViewable = isImage || attachment.contentType === 'application/pdf';

  return (
    <div
      className={clsx(
        'group flex items-center gap-3 p-3',
        'rounded-lg border border-neutral-200 dark:border-neutral-700',
        'bg-white dark:bg-neutral-800',
        'hover:border-neutral-300 dark:hover:border-neutral-600',
        'transition-colors'
      )}
    >
      {/* Thumbnail or icon */}
      <div
        className={clsx(
          'flex h-10 w-10 shrink-0 items-center justify-center',
          'rounded-lg',
          'bg-neutral-100 dark:bg-neutral-700'
        )}
      >
        {isImage && attachment.thumbnailUrl ? (
          <img
            src={attachment.thumbnailUrl}
            alt={attachment.filename}
            className="h-full w-full rounded-lg object-cover"
          />
        ) : (
          <FileIcon className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
        )}
      </div>

      {/* File info */}
      <div className="min-w-0 flex-1">
        <p
          className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100"
          title={attachment.filename}
        >
          {attachment.filename}
        </p>
        <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
          <span>{formatFileSize(attachment.sizeBytes)}</span>
          <span>&bull;</span>
          <span>
            {formatDistanceToNow(new Date(attachment.createdAt), { addSuffix: true })}
          </span>
        </div>
      </div>

      {/* Actions */}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            className={clsx(
              'rounded p-1.5',
              'text-neutral-400 hover:text-neutral-600',
              'dark:text-neutral-500 dark:hover:text-neutral-300',
              'hover:bg-neutral-100 dark:hover:bg-neutral-700',
              'opacity-0 group-hover:opacity-100',
              'transition-all'
            )}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className={clsx(
              'z-50 min-w-[140px] overflow-hidden rounded-md',
              'bg-white dark:bg-neutral-800',
              'border border-neutral-200 dark:border-neutral-700',
              'p-1 shadow-lg'
            )}
            sideOffset={4}
          >
            {isViewable && (
              <DropdownMenu.Item
                onClick={onView}
                className={clsx(
                  'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5',
                  'text-sm text-neutral-700 dark:text-neutral-200',
                  'hover:bg-neutral-100 dark:hover:bg-neutral-700',
                  'outline-none'
                )}
              >
                <Eye className="h-4 w-4" />
                View
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Item
              onClick={onDownload}
              className={clsx(
                'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5',
                'text-sm text-neutral-700 dark:text-neutral-200',
                'hover:bg-neutral-100 dark:hover:bg-neutral-700',
                'outline-none'
              )}
            >
              <Download className="h-4 w-4" />
              Download
            </DropdownMenu.Item>
            <DropdownMenu.Separator className="my-1 h-px bg-neutral-200 dark:bg-neutral-700" />
            <DropdownMenu.Item
              onClick={onDelete}
              className={clsx(
                'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5',
                'text-sm text-red-600 dark:text-red-400',
                'hover:bg-red-50 dark:hover:bg-red-900/20',
                'outline-none'
              )}
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
